import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { ListItem } from "framework7-react";
import { useCallback } from "react";

import { getFragmentData } from "@/generated/graphql";
import {
  ActivityFragment,
  CreateNextActivityByActivityIdDocument,
  EntryFragmentDoc,
  GetUserCategoriesDocument,
  UserCategoryFragmentDoc,
} from "@/generated/graphql/graphql";
import { useBack } from "@/hooks/useBack";
import { useGetEntryInput } from "@/hooks/useGetEntryInput";
import { useNavigate } from "@/hooks/useNavigate";

import { ColorCircle } from "./ColorCircle";

type ActivityStarterButtonProps = {
  activity: ActivityFragment;
  onCompleted: VoidFunction;
};

export function ActivityStarterListItem({ activity, onCompleted }: ActivityStarterButtonProps) {
  const routerBack = useBack();
  const navigate = useNavigate();

  const { data } = useQuery(GetUserCategoriesDocument, { fetchPolicy: "cache-first" });
  const userCategories = getFragmentData(UserCategoryFragmentDoc, data?.userCategories || []);
  const userCategory = userCategories.find(({ categoryKey: category }) => category === activity.categoryKey);

  const [createNextActivity, { loading }] = useMutation(CreateNextActivityByActivityIdDocument);

  const getEntryInput = useGetEntryInput();

  const handleClick = useCallback(async () => {
    const entryInput = await getEntryInput();

    await createNextActivity({
      onCompleted: (data) => {
        if (data.createEntryByActivityId?.entry) {
          const entry = getFragmentData(EntryFragmentDoc, data.createEntryByActivityId.entry);
          routerBack();
          navigate(`/modals/entries/${entry.id}/update-feeling/`);
        }
      },
      variables: {
        activityId: activity.id,
        entryInput,
      },
    }).finally(onCompleted);
  }, [createNextActivity, getEntryInput, navigate, onCompleted, routerBack, activity.id]);

  return (
    <ListItem
      after={userCategory?.name || ""}
      disabled={loading}
      link="#"
      title={activity.name}
      onClick={handleClick}
    >
      <ColorCircle
        color={userCategory?.color || "unset"}
        size={24}
        slot="media"
      />
    </ListItem>
  );
}
